import i18n from "@/i18n";
import TrendSignalsService from "@/services/common/TrendSignalsService";
import ToastMessageType from "@/constants/enums/ToastMessageType";
import { RESET_STATE, SET } from "@/stores/CommumActionTypes";
import {
	SET_SELECTED_FREQUENCY_TREND_SIGNAL,
	SET_LOADING_FREQUENCY_TREND_SIGNAL,
	SET_AVAILABLE_FREQUENCY_TREND_SIGNAL,
	SET_MENU_OPENED,
	TOGGLE_COMMON_GROUP_EXPANDED,
	TOGGLE_DISTINCT_GROUP_EXPANDED,
	ON_TOGGLE_DISTINCT_ASSET_GROUP,
	SET_DISTINCT_ASSET_GROUP,
	SHOW_GLOBAL_SIGNAL_NAME_DIALOG,
	CLOSE_GLOBAL_SIGNAL_NAME_DIALOG,
	LOAD_FREQUENCY_TREND_SIGNALS,
	GET_FREQUENCY_TREND_SIGNAL_SETTINGS,
	ON_TOGGLE_SIGNAL,
	ON_CUSTOM_DOMAIN_COMPARISON,
	SET_FILTER_TEXT,
	SHOW_TOAST_MESSAGE,
	DESELECT_ALL_SIGNALS,
	CLEAR_SELECTED_FREQUENCY_TREND_SIGNALS,
	GET_FREQUENCY_SIGNAL_SETTING_BY_ROUTE_ID
} from "./FrequencyTrendSignalActionsType";
import { ADVANCED_SETTINGS_DRAWER_PATH, TREND_PATH } from "../../ModulePath";

const actions = {
	[SET]: ({ commit }, assets) =>
	{
		commit(SET, assets);
	},

	[SET_SELECTED_FREQUENCY_TREND_SIGNAL]: ({ commit }, assets) =>
	{
		commit(SET_SELECTED_FREQUENCY_TREND_SIGNAL, assets);
	},

	[RESET_STATE]: ({ commit }) =>
	{
		commit(RESET_STATE);
	},
	[SET_MENU_OPENED]: ({ commit }, isOpen) =>
	{
		commit(SET_MENU_OPENED, isOpen);
	},
	[TOGGLE_COMMON_GROUP_EXPANDED]: ({ commit, state }) =>
	{
		commit(TOGGLE_COMMON_GROUP_EXPANDED, !state.CommonGroupExpanded);
	},
	
	[TOGGLE_DISTINCT_GROUP_EXPANDED]: ({ commit, state }) =>
	{
		commit(TOGGLE_DISTINCT_GROUP_EXPANDED, !state.DistinctGroupExpanded);
	},
	
	[ON_TOGGLE_DISTINCT_ASSET_GROUP]: ({ commit }, assetName) =>
	{
		commit(ON_TOGGLE_DISTINCT_ASSET_GROUP, assetName);
	},
	
	[SET_DISTINCT_ASSET_GROUP]: ({ commit }, assetName) =>
	{
		commit(SET_DISTINCT_ASSET_GROUP, assetName);
	},
	
	[SHOW_GLOBAL_SIGNAL_NAME_DIALOG]: ({ commit }) =>
	{
		commit("SET_SHOW_SIGNAL_NAME_DIALOG", true);
	},
	
	[CLOSE_GLOBAL_SIGNAL_NAME_DIALOG]: ({ commit }) =>
	{
		commit("SET_SHOW_SIGNAL_NAME_DIALOG", false);
	},
	[LOAD_FREQUENCY_TREND_SIGNALS]: async({ dispatch, state }) =>
	{
		try
		{
			await dispatch(GET_FREQUENCY_TREND_SIGNAL_SETTINGS);

			if (state.noDataAvailable)
			{
				const message = i18n.t("ValidationMessages.NoTrendFrequencySignalAvailable");
				dispatch(SHOW_TOAST_MESSAGE, { message, type: ToastMessageType.INFO }, { root: true });
			}

			commit(SET_SELECTED_FREQUENCY_TREND_SIGNAL);
		}
		catch (error)
		{
			const message = i18n.t("ErrorMessages.TrendSignalsRequestError");
			dispatch(SHOW_TOAST_MESSAGE, { message, type: ToastMessageType.ERROR }, { root: true });
		}
	},

	[GET_FREQUENCY_TREND_SIGNAL_SETTINGS]: async({
		commit, rootState, dispatch
	}) =>
	{
		commit(SET_LOADING_FREQUENCY_TREND_SIGNAL, true);

		try
		{
			const selectedAssets = rootState.Trend.TrendMenu.TrendAssetSelector.selectedAssets;
			const onlyCalculated = true;

			const trendSettingsData = await TrendSignalsService.GetFrequencyTrendSignalSettingsByAssetsMonitoringElements({
				generatingUnitsMonitoringElementsIds: selectedAssets,
				onlyCalculated,
			});

			if (!trendSettingsData || trendSettingsData.length === 0)
			{
				commit(SET_AVAILABLE_FREQUENCY_TREND_SIGNAL, []);
				await dispatch("FrequencyTrendLimitConfiguration/LimitConfigurator/SET", [], { root: true });
				commit("SET_NO_DATA_AVAILABLE", true);
				return;
			}

			commit(SET_AVAILABLE_FREQUENCY_TREND_SIGNAL, trendSettingsData);
			await dispatch("FrequencyTrendLimitConfiguration/LimitConfigurator/SET", trendSettingsData, { root: true });

			commit("SET_NO_DATA_AVAILABLE", false);
		}
		catch (error)
		{
			commit(SET_AVAILABLE_FREQUENCY_TREND_SIGNAL, []);
			await dispatch("FrequencyTrendLimitConfiguration/LimitConfigurator/SET", [], { root: true });
			commit("SET_NO_DATA_AVAILABLE", true);
			throw error;
		}
		finally
		{
			commit(SET_LOADING_FREQUENCY_TREND_SIGNAL, false);
		}
	},
	[GET_FREQUENCY_SIGNAL_SETTING_BY_ROUTE_ID]({ dispatch, state }, trendFrequencyRouteId)
	{
		if (trendFrequencyRouteId)
		{
			const foundedSignal = state.availableCommonSignals.find(s => s.frequencyTrendSettingId === trendFrequencyRouteId);
			
			if (!!foundedSignal) dispatch(ON_TOGGLE_SIGNAL, foundedSignal);
		}
	},
	[ON_TOGGLE_SIGNAL]: ({
		state, commit, dispatch
	}, signal) =>
	{
		const signalsToToggle = [];

		state.assetsFrequencyTrendSettings.forEach(assetSignals =>
		{
			const commonMatch = (assetSignals.commonSignals || []).filter(s => s.name === signal.name);
			const distinctMatch = (assetSignals.distinctSignals || []).filter(s => s.name === signal.name);

			commonMatch.forEach(s => signalsToToggle.push({
				...s, assetName: assetSignals.name, assetId:   assetSignals.monitoringElementId
			}));
			distinctMatch.forEach(s => signalsToToggle.push({
				...s, assetName: assetSignals.name, assetId:   assetSignals.monitoringElementId
			}));
		});
		const updatedSignals = [ ...(state.selectedFrequencyTrendSettings || []) ];
		signalsToToggle.forEach(s =>
		{
			const index = updatedSignals.findIndex(
				(sel => s.name === sel.name &&
				s.assetName === sel.assetName
				)
			);
			if (index > -1)
			{
				updatedSignals.splice(index, 1);
			}
			else
			{
				updatedSignals.push(s);
			}
		});
		commit(SET_SELECTED_FREQUENCY_TREND_SIGNAL, updatedSignals);
		dispatch(ON_CUSTOM_DOMAIN_COMPARISON);
	},
	[ON_CUSTOM_DOMAIN_COMPARISON]: ({ dispatch, rootState }) =>
	{
		dispatch(`${TREND_PATH}/${RESET_STATE}`, null, { root: true });
		
		const isCustomDomainComparisonChecked =
					rootState.Trend.TrendMenu.AdvancedSettingsDrawer.menu.isCustomDomainComparisonChecked;
		
		if (isCustomDomainComparisonChecked)
		{
			dispatch(
				`${ADVANCED_SETTINGS_DRAWER_PATH}/${TOGGLE_CUSTOM_DOMAIN_COMPARISON}`,
				false,
				{ root: true }
			);
		}
	},
	[SET_FILTER_TEXT]: ({ commit }, text) =>
	{
		commit(SET_FILTER_TEXT, text);
	},
	[SHOW_TOAST_MESSAGE]: ({ commit }, { message, type }) =>
	{
		commit("SET_TOAST_MESSAGE", message, { root: true });
		commit("SET_TOAST_TYPE", type, { root: true });
		commit("SET_SHOW_TOAST", true, { root: true });
	},
	[DESELECT_ALL_SIGNALS]: ({ commit }) =>
	{
		commit(CLEAR_SELECTED_FREQUENCY_TREND_SIGNALS);
	},
};

export default { actions };
